import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, GitBranch, Warning, ArrowRight, GridFour, ChartLineUp, Stack, CurrencyInr, CheckCircle, Terminal, CaretDown, CaretRight, ClockCounterClockwise, ArrowCounterClockwise, ArrowsClockwise } from "@phosphor-icons/react";
import { getMasterOverview, getMasterOrphans, masterMergePreview, masterMergeApply, masterMergeRevert, getMasterLog, getMasterDiff, rebuildCutoffsPreview, rebuildCutoffs, backfillCollegeCodePreview, backfillCollegeCode, backfillCatNormPreview, backfillCatNorm, rebuildFilterCache, getFilterOptions, orphanReconOpen, smReviewOpen } from "../lib/api";
import IngestReconViewer from "../components/IngestReconViewer";
import { toast } from "sonner";
import MatchReviewPanel from "../components/MatchReviewPanel";

const SCHEMA_COLS = [
  { key: "seat_matrix", label: "Seat Matrix", icon: GridFour },
  { key: "allotments",  label: "Allotments",  icon: ChartLineUp },
  { key: "intake",      label: "Intake",      icon: Stack },
  { key: "fees",        label: "Fees",        icon: CurrencyInr },
];

// Tool buckets (data buckets). Each maps to an orphan scope where applicable.
const BUCKETS = [
  { key: "allotments",  label: "Allotments",  icon: ChartLineUp, color: "#EA580C" },
  { key: "seat_matrix", label: "Seat Matrix", icon: GridFour,    color: "#0EA5E9" },
  { key: "intake",      label: "Seat Intake", icon: Stack,       color: "#10B981" },
  { key: "fees",        label: "Fees",        icon: CurrencyInr, color: "#6043EF" },
];

function Cell({ scope, row }) {
  const v = row[scope];
  let text = "—", on = false;
  if (scope === "seat_matrix") { on = v.n > 0; text = on ? `${v.years.length}y·${v.n}` : "—"; }
  else if (scope === "allotments") { on = v.n > 0; text = on ? `${v.years.length}y·${v.n}` : "—"; }
  else if (scope === "intake") { on = v.total != null || v.has_nmc; text = v.total != null ? `${v.total}` : (v.has_nmc ? "✓" : "—"); }
  else if (scope === "fees") { on = v.has; text = on ? `${v.years.length}y ✓` : "—"; }
  return (
    <span className={`inline-block min-w-[52px] text-center px-2 py-0.5 rounded text-[11px] font-mono font-semibold ${on ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}>
      {text}
    </span>
  );
}

function GatePill({ gate }) {
  if (!gate) return null;
  const cls = gate.allowed ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid="master-gate-pill">
      {gate.allowed ? <CheckCircle size={11} weight="fill" /> : <Warning size={11} weight="fill" />}
      Stage {gate.stage} · {Math.round(gate.score * 100)}%
    </span>
  );
}

export default function AdminMaster() {
  const [states, setStates] = useState([]);
  const [state, setState] = useState("");
  const [data, setData] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [orphanRecon, setOrphanRecon] = useState(null);
  const [openingOrphan, setOpeningOrphan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [openBucket, setOpenBucket] = useState(null);
  const [command, setCommand] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [lastParams, setLastParams] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [log, setLog] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [logCode, setLogCode] = useState("");
  const [diff, setDiff] = useState(null);
  const [rebuildPrev, setRebuildPrev] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [backfillPrev, setBackfillPrev] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [catNormPrev, setCatNormPrev] = useState(null);
  const [catNorming, setCatNorming] = useState(false);
  const [bodies, setBodies] = useState([]);
  const [rebuildMenu, setRebuildMenu] = useState(false);
  const [rebuildCourse, setRebuildCourse] = useState("");

  useEffect(() => {
    getFilterOptions().then((r) => {
      const s = r.data.states || [];
      setStates(s);
      setBodies(r.data.counselling_bodies || []);
      setState((cur) => cur || (s.includes("Karnataka") ? "Karnataka" : s[0]) || "");
    }).catch(() => {});
    getMasterOrphans().then((r) => setOrphans(r.data.orphans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!state) return;
    setLoading(true);
    getMasterOverview(state).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [state]);

  const refreshLog = async (code) => {
    try {
      const r = await getMasterLog(code || undefined);
      setLog(r.data.log || []);
    } catch { setLog([]); }
  };

  const runPreview = async (scope, from_key, to_code, force = false) => {
    if (!to_code) { toast.error("No target master for this item"); return; }
    setPreviewing(true);
    setOpenBucket(null);
    setDiff(null);
    setCommand(`unicators merge ${scope} ${to_code} ${from_key}${force ? " --force" : ""}`);
    try {
      const r = await masterMergePreview({ scope, from_key, to_code, force });
      setPreview(r.data);
      setLastParams({ scope, from_key, to_code, force });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const doApply = async () => {
    if (!lastParams) return;
    const blocked = preview && !preview.gate?.allowed;
    const force = blocked || lastParams.force;
    if (blocked && confirmText.trim().toUpperCase() !== lastParams.to_code.toUpperCase()) {
      toast.error(`Type ${lastParams.to_code} to confirm force-apply`);
      return;
    }
    setApplying(true);
    try {
      const r = await masterMergeApply({ ...lastParams, force });
      toast.success(`Merged ${r.data.total_rows} rows → op ${r.data.op_id}`);
      setConfirmOpen(false);
      setConfirmText("");
      setPreview(null);
      setLastParams(null);
      await Promise.all([
        getMasterOrphans().then((rr) => setOrphans(rr.data.orphans || [])).catch(() => {}),
        state && getMasterOverview(state).then((rr) => setData(rr.data)).catch(() => {}),
        logOpen && refreshLog(logCode),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  const doRevert = async (op_id) => {
    try {
      const r = await masterMergeRevert(op_id);
      toast.success(`Reverted op ${op_id} (${r.data.rebuild_bodies?.length || 0} bodies rebuilt)`);
      await Promise.all([
        refreshLog(logCode),
        getMasterOrphans().then((rr) => setOrphans(rr.data.orphans || [])).catch(() => {}),
        state && getMasterOverview(state).then((rr) => setData(rr.data)).catch(() => {}),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    }
  };

  const showDiff = async (op_id) => {
    try {
      const r = await getMasterDiff(op_id);
      setDiff(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diff failed");
    }
  };

  const parseAndPreview = async () => {
    const cmd = command.trim();
    const mLog = cmd.match(/^unicators\s+log(?:\s+(\S+))?$/i);
    if (mLog) { setLogCode(mLog[1] || ""); setLogOpen(true); await refreshLog(mLog[1]); return; }
    const mDiff = cmd.match(/^unicators\s+diff\s+(\S+)$/i);
    if (mDiff) { await showDiff(mDiff[1]); return; }
    const mRebuild = cmd.match(/^unicators\s+update\s+cutoffs\s+(.+?)(?:\s+(MBBS|BDS))?$/i) || cmd.match(/^unicators\s+(.+?)(?:\s+(MBBS|BDS))?\s+update\s+cutoffs$/i);
    if (mRebuild) { await runRebuildPreview(mRebuild[1].trim(), (mRebuild[2] || "").toUpperCase() || null); return; }
    const mBackfill = cmd.match(/^unicators\s+backfill\s+college-code\s+(\S+)$/i);
    if (mBackfill) { await runBackfillPreview(mBackfill[1].trim()); return; }
    const mCatNorm = cmd.match(/^unicators\s+backfill\s+cat-norm\s+(\S+)$/i);
    if (mCatNorm) { await runCatNormPreview(mCatNorm[1].trim()); return; }
    const mFcache = cmd.match(/^unicators\s+rebuild\s+filter-cache(?:\s+(\S+))?$/i);
    if (mFcache) { await runFilterCacheRebuild(mFcache[1] ? mFcache[1].trim() : null); return; }
    const m = cmd.match(/^unicators\s+merge\s+(\S+)\s+(\S+)\s+(\S+)(\s+--force)?$/i);
    if (!m) { toast.error("Format: merge <scope> <TO> <FROM> [--force] | update cutoffs <code|body|all> [MBBS|BDS] | backfill college-code <body> | backfill cat-norm <body> | rebuild filter-cache [body] | log | diff <op_id>"); return; }
    await runPreview(m[1], m[3], m[2], !!m[4]);
  };

  // Completion pool for "update cutoffs <target> [course]" — bodies+all, then MBBS/BDS.
  const completionPool = ["all", ...bodies];
  const handleCommandKey = (e) => {
    if (e.key === "Enter") { parseAndPreview(); return; }
    if (e.key !== "Tab") return;
    const m = command.match(/^(unicators\s+update\s+cutoffs\s+)(.*)$/i);
    if (!m) return;
    e.preventDefault();
    const rest = m[2];
    const parts = rest.split(/\s+/);
    if (parts.length <= 1) {
      const partial = (parts[0] || "").toLowerCase();
      const matches = completionPool.filter((x) => x.toLowerCase().startsWith(partial));
      if (matches.length) setCommand(m[1] + matches[0]);
    } else {
      const partial = (parts[1] || "").toUpperCase();
      const matches = ["MBBS", "BDS"].filter((x) => x.startsWith(partial));
      if (matches.length) setCommand(m[1] + parts[0] + " " + matches[0]);
    }
  };

  const runRebuildPreview = async (target, course = null) => {
    setPreview(null); setLastParams(null); setDiff(null); setRebuildPrev(null); setBackfillPrev(null); setCatNormPrev(null);
    try {
      const r = await rebuildCutoffsPreview(target, course);
      setRebuildPrev({ ...r.data, target, courseArg: course });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild preview failed");
    }
  };

  const doRebuild = async () => {
    if (!rebuildPrev) return;
    setRebuilding(true);
    try {
      const r = await rebuildCutoffs(rebuildPrev.target, rebuildPrev.courseArg);
      toast.success(`Rebuilt: ${r.data.inserted} rows across ${r.data.pairs} college·body pairs (op ${r.data.op_id})`);
      setRebuildPrev(null);
      await Promise.all([
        state && getMasterOverview(state).then((rr) => setData(rr.data)).catch(() => {}),
        logOpen && refreshLog(logCode),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed");
    } finally { setRebuilding(false); }
  };

  const runBackfillPreview = async (body) => {
    setPreview(null); setLastParams(null); setDiff(null); setRebuildPrev(null); setBackfillPrev(null); setCatNormPrev(null);
    try {
      const r = await backfillCollegeCodePreview(body);
      setBackfillPrev({ ...r.data, body });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill preview failed");
    }
  };

  const doBackfill = async () => {
    if (!backfillPrev) return;
    setBackfilling(true);
    try {
      const r = await backfillCollegeCode(backfillPrev.body);
      toast.success(`Backfilled college_code on ${r.data.rows_updated} rows across ${r.data.colleges_touched} colleges (op ${r.data.op_id})`);
      setBackfillPrev(null);
      await Promise.all([
        state && getMasterOverview(state).then((rr) => setData(rr.data)).catch(() => {}),
        logOpen && refreshLog(logCode),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  const runCatNormPreview = async (body) => {
    setPreview(null); setLastParams(null); setDiff(null); setRebuildPrev(null); setBackfillPrev(null); setCatNormPrev(null);
    try {
      const r = await backfillCatNormPreview(body);
      setCatNormPrev({ ...r.data, body });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cat-norm preview failed");
    }
  };

  const doCatNorm = async () => {
    if (!catNormPrev) return;
    setCatNorming(true);
    try {
      const r = await backfillCatNorm(catNormPrev.body);
      toast.success(`Re-stamped category_normalized on ${r.data.rows_updated} rows (${r.data.distinct_new_categories} categories, op ${r.data.op_id})`);
      setCatNormPrev(null);
      if (logOpen) await refreshLog(logCode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cat-norm backfill failed");
    } finally { setCatNorming(false); }
  };

  const runFilterCacheRebuild = async (body) => {
    const t = toast.loading(body ? `Rebuilding filter cache for ${body}…` : "Rebuilding filter cache (all bodies)…");
    try {
      const r = await rebuildFilterCache(body);
      toast.success(`${r.data.message}${r.data.categories != null ? ` — ${r.data.categories} categories` : ""}`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Filter cache rebuild failed", { id: t });
    }
  };

  const cov = data?.registry_coverage;
  const orphansIn = (scope) => orphans.filter((o) => o.scopes && o.scopes[scope]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-master-page">
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-4">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-[#6043EF] mb-2" data-testid="master-back-link">
            <ArrowLeft size={13} weight="bold" /> Admin
          </Link>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#09090B] flex items-center gap-2">
            <GitBranch size={26} weight="bold" className="text-[#6043EF]" /> College Master
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            The canonical branch for each college (from key_registry). Move data between colleges — gated by key_registry, logged, and revertible. <span className="font-semibold text-zinc-600">Phase 2: live merges with git-style log &amp; revert.</span>
          </p>
        </div>

        {/* ── College Match Review (matched/unmatched resolver) ── */}
        <MatchReviewPanel />

        {/* ── College Master table (collapsed; filter line only, NMC-raw style) ── */}
        <div className="border-2 border-zinc-200 rounded-xl overflow-hidden bg-white mb-5" data-testid="master-table-card">
          <div className="px-3 py-2 flex items-center gap-3 flex-wrap border-b border-zinc-100 bg-zinc-50">
            <button onClick={() => setTableOpen((o) => !o)} data-testid="master-table-toggle"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#09090B] hover:text-[#6043EF]">
              {tableOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
              College Master
            </button>
            <select value={state} onChange={(e) => setState(e.target.value)} data-testid="master-state-select"
              className="h-8 px-2.5 border border-zinc-300 rounded-md text-xs font-semibold bg-white focus:border-[#6043EF] outline-none">
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-[11px] font-mono text-zinc-500">{data?.total ?? 0} colleges</span>
            {cov && (
              <span className="text-[11px] text-zinc-500 ml-auto" data-testid="master-coverage">
                key_registry: <b className={cov.missing.length ? "text-rose-600" : "text-emerald-600"}>{cov.in_registry}/{cov.total}</b>
                {cov.missing.length > 0 && <span className="text-rose-600"> · {cov.missing.length} missing</span>}
              </span>
            )}
          </div>
          {tableOpen && (
            <div className="overflow-x-auto max-h-[520px]" data-testid="master-table-body">
              <table className="w-full text-sm">
                <thead className="bg-white sticky top-0 text-[10px] font-bold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <tr>
                    <th className="text-left px-3 py-2 w-10">#</th>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">College</th>
                    <th className="text-left px-3 py-2">Type</th>
                    {SCHEMA_COLS.map((c) => <th key={c.key} className="text-center px-3 py-2">{c.label}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 font-mono">
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-10 text-zinc-400 text-sm">Loading…</td></tr>
                  ) : (data?.colleges || []).map((r, i) => (
                    <tr key={r.code} className="hover:bg-zinc-50" data-testid={`master-row-${r.code}`}>
                      <td className="px-3 py-1.5 text-zinc-400 text-xs">{i + 1}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold ${r.in_registry ? "bg-[#6043EF]/10 text-[#6043EF]" : "bg-rose-100 text-rose-600"}`} title={r.in_registry ? "in key_registry" : "NOT in key_registry"}>
                          <GitBranch size={10} weight="bold" />{r.code}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-sans"><span className="font-semibold text-[#09090B]">{r.name}</span>{r.city && <span className="text-zinc-400 text-xs"> · {r.city}</span>}</td>
                      <td className="px-3 py-1.5 text-xs text-zinc-500 uppercase">{r.type}</td>
                      {SCHEMA_COLS.map((c) => (
                        <td key={c.key} className="px-3 py-1.5 text-center"><Cell scope={c.key} row={r} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Merge console ── */}
        <div className="border-2 border-zinc-200 rounded-xl bg-[#09090B] p-4 mb-5" data-testid="master-console">
          <div className="flex items-center gap-2 mb-2 text-zinc-400">
            <Terminal size={14} weight="bold" /><span className="text-[10px] font-bold uppercase tracking-wider">Merge Console — <span className="text-emerald-400">live (Phase 2)</span></span>
            <button onClick={() => { setLogOpen((o) => { const n = !o; if (n) refreshLog(logCode); return n; }); }}
              data-testid="master-log-toggle"
              className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-[#a78bfa]">
              <ClockCounterClockwise size={13} weight="bold" /> {logOpen ? "Hide log" : "Show log"}
            </button>
          </div>
          <div className="flex gap-2">
            <input
              value={command} onChange={(e) => setCommand(e.target.value)}
              onKeyDown={handleCommandKey}
              placeholder="merge allotments M097 <from-key> · update cutoffs <code|body|all> [MBBS|BDS] · backfill college-code <body> · backfill cat-norm <body> · rebuild filter-cache [body] · log · diff <op_id>"
              data-testid="master-command-input"
              className="flex-1 bg-[#18181B] text-emerald-300 font-mono text-sm px-3 py-2 rounded-lg border border-zinc-700 focus:border-[#6043EF] outline-none placeholder:text-zinc-600"
            />
            <button onClick={parseAndPreview} disabled={previewing} data-testid="master-preview-btn"
              className="h-10 px-5 bg-[#6043EF] text-white font-bold text-xs uppercase tracking-wider rounded-lg disabled:opacity-60 hover:bg-[#4f35d4]">
              {previewing ? "…" : "Preview"}
            </button>
          </div>

          {preview && (
            <div className="mt-3 bg-[#18181B] border border-zinc-700 rounded-lg p-3 text-zinc-300 text-[13px]" data-testid="master-preview">
              {preview.error ? (
                <p className="text-rose-400">{preview.error}</p>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-mono text-zinc-400 truncate max-w-[220px]">{preview.from.key}</span>
                    <span className="text-zinc-500">"{preview.from.name}"</span>
                    <ArrowRight size={13} weight="bold" className="text-[#6043EF]" />
                    <span className="font-mono text-[#a78bfa]">{preview.to.code}</span>
                    <span className="text-zinc-500">"{preview.to.name}"</span>
                    <GatePill gate={preview.gate} />
                  </div>
                  <p className="text-xs text-zinc-400 mb-1">Gate: {preview.gate.reason}</p>
                  <p className="text-sm">Would move <b className="text-emerald-400">{preview.total_rows}</b> rows
                    {Object.entries(preview.counts).map(([k, v]) => <span key={k} className="text-zinc-400 text-xs"> · {k}: {v}</span>)}
                  </p>
                  {preview.rebuild_note && <p className="text-xs text-zinc-500 mt-1">↻ {preview.rebuild_note}</p>}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${preview.would_apply ? "text-emerald-400" : "text-rose-400"}`}>
                      {preview.would_apply ? "Would apply ✓" : "Blocked — needs --force"}
                    </span>
                    {preview.total_rows > 0 && (
                      <button
                        onClick={() => { setConfirmText(""); setConfirmOpen(true); }}
                        data-testid="master-apply-btn"
                        className="ml-auto h-9 px-4 bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg hover:bg-emerald-600">
                        Apply merge
                      </button>
                    )}
                  </div>

                  {confirmOpen && (
                    <div className="mt-3 border border-emerald-700/50 bg-emerald-950/30 rounded-lg p-3" data-testid="master-apply-confirm">
                      <p className="text-xs text-zinc-300 mb-2">
                        {preview.gate?.allowed
                          ? <>Confirm merge of <b className="text-emerald-400">{preview.total_rows}</b> rows into <b className="text-[#a78bfa]">{lastParams?.to_code}</b>. This writes to the DB and rebuilds cutoffs.</>
                          : <>⚠ Gate blocked ({preview.gate?.reason}). To force-apply, type the target code <b className="text-[#a78bfa]">{lastParams?.to_code}</b> below.</>}
                      </p>
                      {!preview.gate?.allowed && (
                        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                          placeholder={lastParams?.to_code} data-testid="master-confirm-input"
                          className="w-full bg-[#0d0d0f] text-rose-300 font-mono text-sm px-3 py-2 rounded-lg border border-rose-700/60 focus:border-rose-500 outline-none mb-2" />
                      )}
                      <div className="flex gap-2">
                        <button onClick={doApply} disabled={applying} data-testid="master-apply-confirm-btn"
                          className="h-9 px-4 bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg disabled:opacity-60 hover:bg-emerald-600">
                          {applying ? "Applying…" : (preview.gate?.allowed ? "Confirm apply" : "Force apply")}
                        </button>
                        <button onClick={() => { setConfirmOpen(false); setConfirmText(""); }} data-testid="master-apply-cancel-btn"
                          className="h-9 px-4 bg-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-wider rounded-lg hover:bg-zinc-700">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {rebuildPrev && (
            <div className="mt-3 bg-[#0d0d0f] border border-emerald-700/50 rounded-lg p-3 text-zinc-300 text-[13px]" data-testid="master-rebuild-preview">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Rebuild cutoffs — preview</span>
                <span className="ml-auto font-mono text-[11px] text-zinc-500">{rebuildPrev.command}</span>
              </div>
              <p className="text-xs text-zinc-400">
                Scope <b className="text-[#a78bfa]">{rebuildPrev.scope?.type}: {rebuildPrev.scope?.value}</b>
                {rebuildPrev.course && rebuildPrev.course !== "ALL" && <> · course <b className="text-amber-400">{rebuildPrev.course}</b></>}
                {" "}· re-derives
                <b className="text-emerald-400"> counselling_results</b> from <b>{rebuildPrev.raw_allotments_rows}</b> raw_allotments rows
                across <b>{rebuildPrev.colleges}</b> college(s) / <b>{rebuildPrev.pairs}</b> college·body pairs.
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                Replaces <b className="text-rose-400">{rebuildPrev.counselling_results_to_replace}</b> existing rows · then rebuilds
                cutoff_summary for: <b className="text-zinc-300">{(rebuildPrev.bodies || []).join(", ")}</b>
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button onClick={doRebuild} disabled={rebuilding} data-testid="master-rebuild-apply-btn"
                  className="h-9 px-4 bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg disabled:opacity-60 hover:bg-emerald-600">
                  {rebuilding ? "Rebuilding…" : "Run rebuild"}
                </button>
                <button onClick={() => setRebuildPrev(null)} disabled={rebuilding} data-testid="master-rebuild-cancel-btn"
                  className="h-9 px-4 bg-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-wider rounded-lg hover:bg-zinc-700">
                  Cancel
                </button>
                <span className="ml-auto text-[10px] text-zinc-600 font-mono">idempotent · re-runnable</span>
              </div>
            </div>
          )}

          {backfillPrev && (
            <div className="mt-3 bg-[#0d0d0f] border border-sky-700/50 rounded-lg p-3 text-zinc-300 text-[13px]" data-testid="master-backfill-preview">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400">Backfill college_code — preview</span>
                <span className="ml-auto font-mono text-[11px] text-zinc-500">{backfillPrev.command}</span>
              </div>
              <p className="text-xs text-zinc-400">
                Body <b className="text-sky-300">{backfillPrev.body}</b> · resolves
                <b className="text-sky-400"> college_code</b> on <b className="text-amber-400">{backfillPrev.rows_to_update}</b> of
                {" "}{backfillPrev.blank_college_code_rows} blank raw_allotments rows from <b>course_code</b> via key_registry
                across <b>{backfillPrev.colleges_touched}</b> colleges
                ({backfillPrev.resolved_bases}/{backfillPrev.distinct_bases} bases resolved).
              </p>
              {backfillPrev.rows_unresolved > 0 && (
                <p className="text-xs text-rose-400 mt-1">
                  {backfillPrev.rows_unresolved} rows left BLANK — unresolved bases: {(backfillPrev.unresolved_bases || []).join(", ")}
                </p>
              )}
              {(backfillPrev.divergent || []).length > 0 && (
                <p className="text-xs text-zinc-500 mt-1">
                  Registry-collapsed (KEA code → canonical master):{" "}
                  {(backfillPrev.divergent).map((d) => <span key={d.base} className="font-mono"> {d.base}→{d.canonical} </span>)}
                </p>
              )}
              <div className="mt-3 flex items-center gap-2">
                <button onClick={doBackfill} disabled={backfilling || backfillPrev.rows_to_update === 0} data-testid="master-backfill-apply-btn"
                  className="h-9 px-4 bg-sky-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg disabled:opacity-60 hover:bg-sky-600">
                  {backfilling ? "Backfilling…" : "Run backfill"}
                </button>
                <button onClick={() => setBackfillPrev(null)} disabled={backfilling} data-testid="master-backfill-cancel-btn"
                  className="h-9 px-4 bg-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-wider rounded-lg hover:bg-zinc-700">
                  Cancel
                </button>
                <span className="ml-auto text-[10px] text-zinc-600 font-mono">code_key only · unmatched left blank · revertible</span>
              </div>
            </div>
          )}

          {catNormPrev && (
            <div className="mt-3 bg-[#0d0d0f] border border-violet-700/50 rounded-lg p-3 text-zinc-300 text-[13px]" data-testid="master-catnorm-preview">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-violet-400">Backfill category_normalized — preview</span>
                <span className="ml-auto font-mono text-[11px] text-zinc-500">{catNormPrev.command}</span>
              </div>
              <p className="text-xs text-zinc-400">
                Body <b className="text-violet-300">{catNormPrev.body}</b> · re-stamps
                <b className="text-violet-400"> category_normalized</b> from <b>category_raw</b> on
                {" "}<b className={catNormPrev.rows_to_change > 0 ? "text-amber-400" : "text-emerald-400"}>{catNormPrev.rows_to_change}</b> of
                {" "}{catNormPrev.rows_with_category_raw} rows → <b>{catNormPrev.distinct_new_categories}</b> categories.
                {catNormPrev.noop && <span className="text-emerald-400 font-bold"> NO-OP (already normalized)</span>}
              </p>
              {(catNormPrev.transitions || []).length > 0 && (
                <div className="mt-2 max-h-32 overflow-auto text-[11px] font-mono text-zinc-500 grid grid-cols-2 gap-x-4">
                  {(catNormPrev.transitions).map((t) => (
                    <div key={t.change}><span className="text-zinc-300">{t.change}</span> <span className="text-zinc-600">×{t.rows}</span></div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2">
                <button onClick={doCatNorm} disabled={catNorming || catNormPrev.rows_to_change === 0} data-testid="master-catnorm-apply-btn"
                  className="h-9 px-4 bg-violet-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg disabled:opacity-60 hover:bg-violet-600">
                  {catNorming ? "Re-stamping…" : "Run re-stamp"}
                </button>
                <button onClick={() => setCatNormPrev(null)} disabled={catNorming} data-testid="master-catnorm-cancel-btn"
                  className="h-9 px-4 bg-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-wider rounded-lg hover:bg-zinc-700">
                  Cancel
                </button>
                <span className="ml-auto text-[10px] text-zinc-600 font-mono">recompute from category_raw · revertible</span>
              </div>
            </div>
          )}


          {diff && (
            <div className="mt-3 bg-[#18181B] border border-[#6043EF]/40 rounded-lg p-3 text-zinc-300 text-[13px]" data-testid="master-diff">
              <div className="flex items-center gap-2 mb-2">
                <GitBranch size={13} weight="bold" className="text-[#a78bfa]" />
                <span className="font-mono text-[#a78bfa]">op {diff.op_id}</span>
                <span className="text-zinc-500">· {diff.scope}</span>
                {diff.reverted && <span className="text-[10px] font-bold uppercase text-amber-400">reverted</span>}
                <button onClick={() => setDiff(null)} className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-300 uppercase font-bold">close</button>
              </div>
              <p className="text-xs"><span className="font-mono text-zinc-400">{diff.from_key}</span> "{diff.from_name}" <ArrowRight size={11} weight="bold" className="inline text-[#6043EF]" /> <span className="font-mono text-[#a78bfa]">{diff.to_code}</span> "{diff.to_name}"</p>
              <p className="text-xs text-zinc-400 mt-1">moved <b className="text-emerald-400">{diff.total_rows}</b> rows · by {diff.actor} · {new Date(diff.at).toLocaleString()}</p>
              <table className="w-full text-[11px] font-mono mt-2">
                <thead className="text-zinc-500"><tr><th className="text-left">collection</th><th className="text-right">from (before→after)</th><th className="text-right">to (before→after)</th></tr></thead>
                <tbody>
                  {Object.keys(diff.before || {}).map((c) => (
                    <tr key={c} className="border-t border-zinc-800">
                      <td className="text-left py-0.5">{c}</td>
                      <td className="text-right text-zinc-400">{diff.before[c].from} → {diff.after[c].from}</td>
                      <td className="text-right text-emerald-400">{diff.before[c].to} → {diff.after[c].to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {logOpen && (
            <div className="mt-3 bg-[#18181B] border border-zinc-700 rounded-lg p-3" data-testid="master-log-panel">
              <div className="flex items-center gap-2 mb-2 text-zinc-400">
                <ClockCounterClockwise size={13} weight="bold" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Op log{logCode ? ` · ${logCode}` : ""}</span>
                <span className="ml-auto text-[10px] font-mono text-zinc-600">{log.length} ops</span>
              </div>
              {log.length === 0 ? (
                <p className="text-xs text-zinc-500 py-2">No operations yet.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto divide-y divide-zinc-800">
                  {log.map((e) => (
                    <div key={e.op_id} className="py-2 flex items-center gap-2 text-[12px]" data-testid={`master-log-row-${e.op_id}`}>
                      <span className="font-mono text-[#a78bfa] shrink-0">{e.op_id}</span>
                      <span className="text-zinc-400 truncate">{e.from_name} → {e.to_code}</span>
                      <span className="text-[10px] font-mono text-emerald-400 shrink-0">{e.total_rows}r</span>
                      {e.reverted && <span className="text-[9px] font-bold uppercase text-amber-400 shrink-0">reverted</span>}
                      <div className="ml-auto flex items-center gap-1.5 shrink-0">
                        <button onClick={() => showDiff(e.op_id)} data-testid={`master-diff-btn-${e.op_id}`}
                          className="text-[10px] font-bold uppercase text-zinc-400 hover:text-[#a78bfa]">diff</button>
                        {!e.reverted && (
                          <button onClick={() => doRevert(e.op_id)} data-testid={`master-revert-btn-${e.op_id}`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase text-zinc-400 hover:text-rose-400">
                            <ArrowCounterClockwise size={11} weight="bold" /> revert
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Tool buckets (per data-bucket, pick from unattached data) ── */}
        <div data-testid="master-tools">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 mb-2">Tools — move unattached data by bucket</div>
          <div className="mb-3">
            <button
              onClick={async () => {
                setOpeningOrphan(true);
                try { const r = await orphanReconOpen(); setOrphanRecon(r.data); }
                catch (e) { alert("Failed to open orphan reconciler"); }
                finally { setOpeningOrphan(false); }
              }}
              disabled={openingOrphan}
              data-testid="open-orphan-reconciler-btn"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#09090B] text-white text-sm font-bold hover:bg-[#6043EF] transition-colors disabled:opacity-50"
            >
              {openingOrphan ? "Scanning…" : "⚠ Open Orphan Reconciler"}
            </button>
            <span className="ml-3 text-xs text-zinc-500">Codes that show as college links but 404 on click — resolve to an existing master or create one.</span>
          </div>
          <div className="mb-3">
            <button
              onClick={async () => { setOpeningOrphan(true); try { const r = await smReviewOpen(); setOrphanRecon(r.data); } catch { alert("Failed to open SM reconciler"); } finally { setOpeningOrphan(false); } }}
              disabled={openingOrphan}
              data-testid="open-sm-reconciler-btn"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#09090B] text-white text-sm font-bold hover:bg-[#6043EF] transition-colors disabled:opacity-50">
              {openingOrphan ? "Scanning…" : "⚠ Open MCC Seat-Matrix Reconciler"}
            </button>
            <span className="ml-3 text-xs text-zinc-500">MCC SM colleges that didn't resolve — match/create, then re-ingest to populate seats.</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {BUCKETS.map((b) => {
              const Icon = b.icon;
              const items = orphansIn(b.key);
              const isOpen = openBucket === b.key;
              return (
                <div key={b.key} className="relative">
                  <button
                    onClick={() => setOpenBucket(isOpen ? null : b.key)}
                    data-testid={`tool-bucket-${b.key}`}
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border-2 border-zinc-200 bg-white text-sm font-bold text-[#09090B] hover:border-[#09090B] transition-colors"
                  >
                    <Icon size={16} weight="bold" style={{ color: b.color }} />
                    {b.label}
                    <span className="text-[10px] font-mono bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{items.length}</span>
                    <CaretDown size={12} weight="bold" className={`text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="absolute z-20 mt-1.5 w-[340px] bg-white border-2 border-zinc-200 rounded-xl shadow-xl overflow-hidden" data-testid={`tool-dropdown-${b.key}`}>
                      <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                        Unattached · {b.label}
                      </div>
                      {items.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-zinc-400">No unattached {b.label.toLowerCase()} data.</div>
                      ) : (
                        <div className="max-h-64 overflow-y-auto divide-y divide-zinc-100">
                          {items.map((o) => {
                            const st = o.suggested_target;
                            return (
                              <button
                                key={o.key}
                                onClick={() => runPreview(b.key, o.key, st?.code, st ? !st.match?.allowed : false)}
                                disabled={!st}
                                data-testid={`tool-item-${b.key}-${o.key}`}
                                className="w-full text-left px-3 py-2.5 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-[#09090B] truncate">{o.name || "(no name)"}</span>
                                  <span className="text-[10px] font-mono bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded ml-auto shrink-0">{o.scopes[b.key]} rows</span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-1 text-[11px] text-zinc-500">
                                  <span className="font-mono truncate max-w-[150px]">{o.key}</span>
                                  {st ? (
                                    <span className="inline-flex items-center gap-1 text-[#6043EF] font-semibold ml-auto shrink-0">
                                      <ArrowRight size={11} weight="bold" /> {st.code}
                                    </span>
                                  ) : (
                                    <span className="ml-auto text-zinc-400 shrink-0">no target</span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Rebuild cutoffs — re-derive counselling_results + cutoff_summary from raw_allotments */}
            <div className="relative">
              <button
                onClick={() => setRebuildMenu((o) => !o)}
                data-testid="tool-rebuild-cutoffs"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 text-sm font-bold text-emerald-800 hover:border-emerald-500 transition-colors"
              >
                <ArrowsClockwise size={16} weight="bold" className="text-emerald-600" />
                Rebuild Cutoffs
                <CaretDown size={12} weight="bold" className={`text-emerald-500 transition-transform ${rebuildMenu ? "rotate-180" : ""}`} />
              </button>
              {rebuildMenu && (
                <div className="absolute z-20 mt-1.5 w-[300px] bg-white border-2 border-zinc-200 rounded-xl shadow-xl overflow-hidden" data-testid="tool-rebuild-dropdown">
                  <div className="px-3 py-2 border-b border-zinc-100">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Re-derive from raw_allotments</div>
                    <div className="flex gap-1" data-testid="rebuild-course-toggle">
                      {[["", "All courses"], ["MBBS", "MBBS"], ["BDS", "BDS"]].map(([val, lbl]) => (
                        <button key={val || "all"} onClick={() => setRebuildCourse(val)}
                          data-testid={`rebuild-course-${val || "all"}`}
                          className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${rebuildCourse === val ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-zinc-100">
                    <button
                      onClick={() => { setRebuildMenu(false); const c = rebuildCourse; setCommand(`unicators update cutoffs all${c ? " " + c : ""}`); runRebuildPreview("all", c || null); }}
                      data-testid="tool-rebuild-all"
                      className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 font-semibold text-[#09090B] flex items-center gap-2"
                    >
                      <span className="text-[10px] font-mono bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">ALL</span>
                      Every counselling body{rebuildCourse ? ` · ${rebuildCourse}` : ""}
                    </button>
                    {bodies.map((bd) => (
                      <button
                        key={bd}
                        onClick={() => { setRebuildMenu(false); const c = rebuildCourse; setCommand(`unicators update cutoffs ${bd}${c ? " " + c : ""}`); runRebuildPreview(bd, c || null); }}
                        data-testid={`tool-rebuild-body-${bd}`}
                        className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 text-sm text-[#09090B]"
                      >
                        {bd}{rebuildCourse ? <span className="text-amber-600 font-mono text-[11px]"> · {rebuildCourse}</span> : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {orphanRecon && (
        <IngestReconViewer
          ingestId={orphanRecon.ingest_id}
          filename="Orphaned codes · all bodies"
          onClose={() => { setOrphanRecon(null); getMasterOrphans().then((r) => setOrphans(r.data.orphans || [])).catch(() => {}); }}
        />
      )}
    </div>
  );
}
